import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import * as db from '../services/dbService';

interface AuthContextType {
  session: Session | null;
  userId: string;
  loading: boolean;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  deletionDueAt: string | null;
  restoreAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletionDueAt, setDeletionDueAt] = useState<string | null>(null);
  const [statusCheckedFor, setStatusCheckedFor] = useState<string | null>(null);
  const userId = session?.user?.id ?? '';

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    db.setPendingDeletionHandler(setDeletionDueAt);
    return () => db.setPendingDeletionHandler(null);
  }, []);

  useEffect(() => {
    if (!userId) {
      setDeletionDueAt(null);
      setStatusCheckedFor(null);
      return;
    }

    let active = true;
    db.fetchAccountStatus()
      .then(status => { if (active) setDeletionDueAt(status.deletionDueAt); })
      .catch(() => { if (active) setDeletionDueAt(null); })
      .finally(() => { if (active) setStatusCheckedFor(userId); });

    return () => {
      active = false;
    };
  }, [userId]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const deleteAccount = async () => {
    await db.deleteAccount();
    await supabase.auth.signOut();
  };

  const restoreAccount = async () => {
    await db.restoreAccount();
    setDeletionDueAt(null);
  };

  const value: AuthContextType = {
    session,
    userId,
    loading: loading || (Boolean(userId) && statusCheckedFor !== userId),
    signOut,
    deleteAccount,
    deletionDueAt,
    restoreAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
